import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { FEATURE_CODES } from "../catalog/features.ts";
import { PERMISSION_KEYS } from "../catalog/permissions.ts";

/**
 * Reports (`09-reports.md`, `04-api-surface.md` § Reports).
 *
 * One route, one descriptor per report. The source has around forty
 * near-identical report endpoints *(verified)* — the single biggest source of
 * duplication in its API — and collapsing them is only possible if a report is
 * data rather than code: a key, the feature that sells it, the columns it
 * returns, and how to run it.
 */

/** What a column holds, so a client can align, format and total it correctly. */
export const COLUMN_KINDS = ["text", "number", "money", "percent", "quantity", "date"] as const;
export type ColumnKind = (typeof COLUMN_KINDS)[number];

export const ReportColumnSchema = z.object({
  key: z.string(),
  /** A message key, never an English string: "VAT" is not a column header. */
  labelKey: z.string(),
  kind: z.enum(COLUMN_KINDS),
  /** Whether the column picker offers it, and whether it starts selected. */
  isDefault: z.boolean(),
  /**
   * Whether the totals row adds this column up.
   *
   * Off for anything already derived from other columns — an average, a rank,
   * a turn time. Adding two averages gives a number that is not an average of
   * anything, and the sum of rank 1 and rank 2 is 3. A percentage is never
   * totalled for the same reason and does not need the flag, because its kind
   * already says so.
   */
  isTotalled: z.boolean().default(true),
});
export type ReportColumn = z.infer<typeof ReportColumnSchema>;

export const ReportDescriptorSchema = z.object({
  key: z.string(),
  labelKey: z.string(),
  feature: z.enum(FEATURE_CODES),
  /**
   * A permission this report needs *beyond* `reports:read`, or null.
   *
   * Only the cost and profit reports carry one, so that a merchant can hand
   * out the reports surface without handing out the payroll with it. Owner and
   * Manager hold both by default (`07-entitlements-rbac.md` § roles); the key
   * exists for the supervisor role a merchant writes themselves.
   */
  permission: z.enum(PERMISSION_KEYS).nullable().default(null),
  /** Whether rows can be merged across the period, or across stores. */
  supportsConsolidate: z.boolean(),
  /** Whether the report groups by something the caller chooses. */
  groupBy: z.array(z.string()),
  columns: z.array(ReportColumnSchema),
});
export type ReportDescriptor = z.infer<typeof ReportDescriptorSchema>;

/**
 * Every report shares this frame. The dates are **business days**, not
 * calendar days: a restaurant's Saturday ends at 3am on Sunday, and a report
 * that split it at midnight would be answering a different question than the
 * one the owner asked.
 */
export const ReportQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a business date"),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a business date"),
  /** Merge rows across the period rather than keeping each day separate. */
  consolidate: z.enum(["true", "false"]).default("true"),
  groupBy: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  size: z.coerce.number().int().positive().max(500).default(50),
});

/** A row is whatever the descriptor's columns say it is. */
export const ReportRowSchema = z.record(z.string(), z.union([z.string(), z.number(), z.null()]));

export const ReportResultSchema = z.object({
  key: z.string(),
  from: z.string(),
  to: z.string(),
  columns: z.array(ReportColumnSchema),
  rows: z.array(ReportRowSchema),
  /** Column totals where a total means anything. */
  totals: ReportRowSchema.nullable(),
  page: z.object({
    total: z.number().int(),
    size: z.number().int(),
    current: z.number().int(),
    pages: z.number().int(),
  }),
});
export type ReportResult = z.infer<typeof ReportResultSchema>;

/** The column picker, persisted per user per report *(verified)*. */
export const ReportConfigSchema = z.object({
  reportKey: z.string(),
  columns: z.array(z.string()),
});

export const EXPORT_FORMATS = ["csv", "json"] as const;
export const EXPORT_STATUSES = ["queued", "running", "ready", "failed"] as const;

export const ExportTaskSchema = z.object({
  id: z.uuid(),
  reportKey: z.string(),
  format: z.enum(EXPORT_FORMATS),
  status: z.enum(EXPORT_STATUSES),
  from: z.string(),
  to: z.string(),
  rowCount: z.number().int().nullable(),
  error: z.string().nullable(),
  requestedByName: z.string().nullable(),
  requestedAt: z.string(),
  completedAt: z.string().nullable(),
  /**
   * When the file goes away, and once it has, when it went — a stamp in the
   * past means the file is gone. Null on an export that predates retention,
   * whose file is still there.
   */
  contentExpiresAt: z.string().nullable(),
});
export type ExportTask = z.infer<typeof ExportTaskSchema>;

export const RequestExportSchema = z.object({
  format: z.enum(EXPORT_FORMATS).default("csv"),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  consolidate: z.enum(["true", "false"]).default("true"),
  groupBy: z.string().optional(),
  /** Which columns to include. Empty means the report's defaults. */
  columns: z.array(z.string()).default([]),
});

/** The header badge's cheap poll *(verified — `has-pending`)*. */
export const PendingExportsSchema = z.object({ pending: z.number().int() });

// ── The merchant dashboard (`05-merchant-backoffice.md` § 1) ────────────────

const Money = z.string().regex(/^-?\d+$/, "minor units, as digits");

/**
 * A figure and what it was last time. The comparison is **same-weekday**, not
 * same-date *(verified — "VS. Last Sun")*, which is the right shape for a
 * restaurant: a Saturday is nothing like the Friday before it.
 */
export const StoreDashboardTileSchema = z.object({
  key: z.string(),
  value: Money,
  previous: Money,
  /** `value − previous`. Signed, so a client need not subtract strings. */
  change: Money,
});

export const StoreDashboardCountSchema = z.object({
  key: z.string(),
  value: z.number().int(),
  previous: z.number().int(),
  change: z.number().int(),
});

export const StoreDashboardSchema = z.object({
  from: z.string(),
  to: z.string(),
  /** The period the figures are compared against. */
  comparedFrom: z.string(),
  comparedTo: z.string(),
  currency: z.enum(CURRENCY_CODES),
  money: z.array(StoreDashboardTileSchema),
  counts: z.array(StoreDashboardCountSchema),
  byMethod: z.array(z.object({ methodCode: z.string(), methodName: z.string(), net: Money })),
  ranking: z.array(z.object({ name: z.string(), quantity: z.string(), net: Money })),
  daily: z.array(
    z.object({
      businessDate: z.string(),
      total: Money,
      received: Money,
      discount: Money,
      orders: z.number().int(),
    }),
  ),
  /** When these figures were computed, for the "Updated …" line. */
  updatedAt: z.string(),
});
export type StoreDashboard = z.infer<typeof StoreDashboardSchema>;

export const StoreDashboardQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
